/**
 * Upload Foodora invoice PDF to storage bucket foodora_invoices and insert row to foodora_invoices table.
 */
import { supabase } from '../../../lib/supabase';

const STORAGE_BUCKET = 'foodora_invoices';
const MAX_SIZE_MB = 10;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

/**
 * Generate unique filename for PDF (UUID + .pdf)
 * Uses crypto.randomUUID() - built-in Web API
 */
function generateUniqueFilename() {
  const uuid = crypto.randomUUID();
  return `${uuid}.pdf`;
}

/**
 * Upload Foodora invoice PDF.
 * @param {File} file - PDF file
 * @returns {Promise<{ success: boolean, error?: string, id?: number }>}
 */
export async function uploadFoodoraInvoice(file) {
  try {
    if (!file) {
      throw new Error('No file provided');
    }
    if (file.type !== 'application/pdf' && !file.name?.toLowerCase().endsWith('.pdf')) {
      throw new Error('Only PDF files are supported');
    }
    if (file.size > MAX_SIZE) {
      throw new Error(`File is larger than ${MAX_SIZE_MB}MB`);
    }

    const filename = generateUniqueFilename();

    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(filename, file, {
        contentType: 'application/pdf',
        upsert: false,
      });

    if (storageError) {
      throw new Error(`Failed to upload PDF: ${storageError.message}`);
    }

    const { data: row, error: insertError } = await supabase
      .from('foodora_invoices')
      .insert({
        file: filename,
        status: 'pending',
      })
      .select('id')
      .single();

    if (insertError) {
      throw new Error(`Failed to save invoice record: ${insertError.message}`);
    }

    return { success: true, id: row?.id };
  } catch (err) {
    return { success: false, error: err.message };
  }
}
